import { useEffect, useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  assetCreditSourceSchema,
  vendorInvoiceAccountingPreviewSchema,
  type AssetCreditReview,
  type VendorInvoiceDetail,
} from '@erp/shared';
import { apiFetch } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export function VendorAssetCreditEditor({
  sourceInvoiceId,
  invoice,
  initial,
  onChange,
}: {
  sourceInvoiceId: string;
  invoice: VendorInvoiceDetail;
  initial: AssetCreditReview | null;
  onChange: (value: AssetCreditReview | null, changed: boolean) => void;
}) {
  const { t } = useTranslation(),
    prefix = useId();
  const [value, setValue] = useState<AssetCreditReview | null>(initial);
  const source = useQuery({
    queryKey: ['asset-credit-source', sourceInvoiceId],
    enabled: Boolean(sourceInvoiceId),
    queryFn: () =>
      apiFetch(
        `/api/v1/accounting/ledger/assets/credit-source/${sourceInvoiceId}`,
        assetCreditSourceSchema,
      ),
  });
  const credit = useQuery({
    queryKey: ['vendor-invoice-accounting', invoice.id, 'asset-credit-review'],
    enabled: Boolean(source.data?.assets.length),
    queryFn: () =>
      apiFetch(
        `/api/v1/procurement/vendor-invoices/${invoice.id}/accounting-preview`,
        vendorInvoiceAccountingPreviewSchema,
      ),
  });
  useEffect(() => {
    if (!source.data) return;
    if (!source.data.assets.length) {
      setValue(null);
      onChange(null, false);
      return;
    }
    if (initial) {
      setValue(initial);
      onChange(initial, false);
      return;
    }
    const sourceAllocations = source.data.assets.flatMap((asset) =>
      asset.sourceAllocations.length
        ? asset.sourceAllocations.map((allocation) => ({ ...allocation, assetId: asset.id }))
        : source.data.rows.filter((row) => row.accountId === asset.assetAccountId).length === 1
          ? [
              {
                assetId: asset.id,
                sourcePosition:
                  source.data.rows.find((row) => row.accountId === asset.assetAccountId)
                    ?.sourcePosition ?? 1,
                amount: asset.cost,
              },
            ]
          : [],
    );
    const proposed: AssetCreditReview = { sourceAllocations, allocations: [] };
    setValue(proposed);
    onChange(proposed, false);
  }, [source.data, initial, onChange]);
  const change = (next: AssetCreditReview) => {
    setValue(next);
    onChange(next, true);
  };
  if (!sourceInvoiceId) return null;
  if (source.data && !source.data.assets.length) return null;
  if (source.isError || credit.isError)
    return (
      <div role="alert">
        <p>{t((source.error ?? credit.error)?.message || 'ledgerClosing.validation')}</p>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            void source.refetch();
            void credit.refetch();
          }}
        >
          {t('ledgerClosing.retry')}
        </Button>
      </div>
    );
  if (source.isPending || credit.isPending)
    return <p role="status">{t('supplierCredits.loading')}</p>;
  if (!source.data?.assets.length || !value) return null;
  const data = source.data;
  const sourceOptions = data.rows.map((row) => (
    <option key={row.sourcePosition} value={row.sourcePosition}>
      {row.sourcePosition} · {row.accountNumber} · {row.accountName} · {row.homeCost}
    </option>
  ));
  const creditOptions = credit.data?.effectiveSplits.map((row) => (
    <option key={row.position} value={row.position}>
      {row.position} · {row.netAmount}
    </option>
  ));
  return (
    <section
      className="space-y-3 rounded border p-3"
      aria-label={t('ledgerClosing.assetCredit.title')}
    >
      <h3 className="font-semibold">{t('ledgerClosing.assetCredit.title')}</h3>
      <p className="text-sm">{t('ledgerClosing.assetCredit.explanation')}</p>
      <p className="text-sm">{t('ledgerClosing.assetCredit.dependencies')}</p>
      {data.assets.map((asset) => (
        <div key={asset.id} className="space-y-2 rounded border p-2">
          <Link className="underline" to={`/finance/assets?q=${encodeURIComponent(asset.code)}`}>
            {asset.code} · {asset.name}
          </Link>
          <p>
            {t('ledgerClosing.assetCredit.originalCost')}: {asset.cost} ·{' '}
            {t('ledgerClosing.assetCredit.currentCost')}: {asset.currentCost}
          </p>
          {asset.dependencies.map((dependency) => (
            <p key={dependency.eventId}>
              {dependency.effectiveDate} · {t(`ledgerClosing.${dependency.kind}`)}
            </p>
          ))}
          {value.sourceAllocations.map((allocation, index) =>
            allocation.assetId !== asset.id ? null : (
              <div key={index} className="grid gap-2 sm:grid-cols-3">
                <Label htmlFor={`${prefix}-source-${index}`}>
                  {t('ledgerClosing.assetCredit.sourcePosition')}
                </Label>
                <select
                  id={`${prefix}-source-${index}`}
                  className="min-h-11 rounded border bg-background px-2"
                  disabled={Boolean(asset.sourceAllocations.length)}
                  value={allocation.sourcePosition}
                  onChange={(event) =>
                    change({
                      ...value,
                      sourceAllocations: value.sourceAllocations.map((row, at) =>
                        at === index ? { ...row, sourcePosition: Number(event.target.value) } : row,
                      ),
                    })
                  }
                >
                  {sourceOptions}
                </select>
                <Input
                  aria-label={t('ledgerClosing.assetCredit.sourceAllocation')}
                  disabled={Boolean(asset.sourceAllocations.length)}
                  inputMode="decimal"
                  value={allocation.amount}
                  onChange={(event) =>
                    change({
                      ...value,
                      sourceAllocations: value.sourceAllocations.map((row, at) =>
                        at === index
                          ? { ...row, amount: event.target.value.replace(',', '.') }
                          : row,
                      ),
                    })
                  }
                />
                {!asset.sourceAllocations.length ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      change({
                        ...value,
                        sourceAllocations: value.sourceAllocations.filter((_, at) => at !== index),
                      })
                    }
                  >
                    {t('ledgerClosing.remove')}
                  </Button>
                ) : null}
              </div>
            ),
          )}
          {!asset.sourceAllocations.length ? (
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                change({
                  ...value,
                  sourceAllocations: [
                    ...value.sourceAllocations,
                    {
                      assetId: asset.id,
                      sourcePosition:
                        data.rows.find((row) => row.accountId === asset.assetAccountId)
                          ?.sourcePosition ?? 1,
                      amount: '',
                    },
                  ],
                })
              }
            >
              {t('ledgerClosing.add')}
            </Button>
          ) : null}
        </div>
      ))}
      <h4>{t('ledgerClosing.assetCredit.reduction')}</h4>
      {value.allocations.map((allocation, index) => (
        <div key={index} className="space-y-2 rounded border p-2">
          <Label htmlFor={`${prefix}-credit-${index}`}>
            {t('ledgerClosing.assetCredit.creditPosition')}
          </Label>
          <select
            id={`${prefix}-credit-${index}`}
            className="min-h-11 w-full rounded border bg-background px-2"
            value={allocation.creditPosition}
            onChange={(event) =>
              change({
                ...value,
                allocations: value.allocations.map((row, at) =>
                  at === index ? { ...row, creditPosition: Number(event.target.value) } : row,
                ),
              })
            }
          >
            {creditOptions}
          </select>
          <Label htmlFor={`${prefix}-original-${index}`}>
            {t('ledgerClosing.assetCredit.sourcePosition')}
          </Label>
          <select
            id={`${prefix}-original-${index}`}
            className="min-h-11 w-full rounded border bg-background px-2"
            value={allocation.sourcePosition}
            onChange={(event) =>
              change({
                ...value,
                allocations: value.allocations.map((row, at) =>
                  at === index ? { ...row, sourcePosition: Number(event.target.value) } : row,
                ),
              })
            }
          >
            {sourceOptions}
          </select>
          <Label htmlFor={`${prefix}-asset-${index}`}>{t('ledgerClosing.assets')}</Label>
          <select
            id={`${prefix}-asset-${index}`}
            className="min-h-11 w-full rounded border bg-background px-2"
            value={allocation.assetId ?? ''}
            onChange={(event) =>
              change({
                ...value,
                allocations: value.allocations.map((row, at) =>
                  at === index ? { ...row, assetId: event.target.value || null } : row,
                ),
              })
            }
          >
            <option value="">{t('ledgerClosing.assetCredit.unregistered')}</option>
            {data.assets.map((asset) => (
              <option key={asset.id} value={asset.id}>
                {asset.code} · {asset.name}
              </option>
            ))}
          </select>
          <Input
            aria-label={t('ledgerClosing.assetCredit.reduction')}
            inputMode="decimal"
            value={allocation.amount}
            onChange={(event) =>
              change({
                ...value,
                allocations: value.allocations.map((row, at) =>
                  at === index ? { ...row, amount: event.target.value.replace(',', '.') } : row,
                ),
              })
            }
          />
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              change({ ...value, allocations: value.allocations.filter((_, at) => at !== index) })
            }
          >
            {t('ledgerClosing.remove')}
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        onClick={() =>
          change({
            ...value,
            allocations: [
              ...value.allocations,
              {
                assetId: data.assets.length === 1 ? (data.assets[0]?.id ?? null) : null,
                sourcePosition: data.rows[0]?.sourcePosition ?? 1,
                creditPosition: credit.data?.effectiveSplits[0]?.position ?? 1,
                amount: '',
              },
            ],
          })
        }
      >
        {t('ledgerClosing.add')}
      </Button>
    </section>
  );
}
